import type { RevitFamily } from '../../api/revit/types';

export interface FamilySnapshotScope {
  cacheScopeKey: string;
  parameterKeys: string[];
  familyScopeKeys: string[];
  intentKey: string;
}

export function cloneFamilySnapshot(
  families: RevitFamily[],
  requestedParameterKeys?: ReadonlySet<string>
): RevitFamily[] {
  return families.map((family) => ({
    ...family,
    parameters: Array.isArray(family.parameters)
      ? family.parameters
          .filter((parameter) =>
            !requestedParameterKeys || requestedParameterKeys.has(parameter.name.trim().toLowerCase())
          )
          .map((parameter) => ({ ...parameter }))
      : [],
    types: Array.isArray(family.types)
      ? family.types.map((familyType) => ({
          ...familyType,
          parameterValues: Array.isArray(familyType.parameterValues)
            ? familyType.parameterValues
                .filter((parameterValue) =>
                  !requestedParameterKeys || requestedParameterKeys.has(
                    parameterValue.parameterName.trim().toLowerCase()
                  )
                )
                .map((parameterValue) => ({ ...parameterValue }))
            : [],
        }))
      : [],
  }));
}

function hasSameValues(left: string[], right: string[]): boolean {
  return left.length === right.length && left.every((value, index) => value === right[index]);
}

function canReuseFamilySnapshotScope(
  candidate: FamilySnapshotScope,
  requested: FamilySnapshotScope
): boolean {
  if (
    candidate.cacheScopeKey !== requested.cacheScopeKey ||
    candidate.intentKey !== requested.intentKey
  ) {
    return false;
  }
  if (requested.familyScopeKeys.length === 0) {
    if (candidate.familyScopeKeys.length > 0) return false;
  } else {
    const candidateFamilyScopeKeys = new Set(candidate.familyScopeKeys);
    if (
      candidate.familyScopeKeys.length === 0 ||
      !requested.familyScopeKeys.every((familyKey) => candidateFamilyScopeKeys.has(familyKey))
    ) {
      return false;
    }
  }
  const candidateParameterKeys = new Set(candidate.parameterKeys);
  return requested.parameterKeys.every((parameterKey) => candidateParameterKeys.has(parameterKey));
}

export function findSmallestReusableEntry<T extends { scope: FamilySnapshotScope }>(
  entries: Iterable<T>,
  requestedScope: FamilySnapshotScope,
  canUseEntry: (entry: T) => boolean = () => true
): T | null {
  let match: T | null = null;
  for (const entry of entries) {
    if (!canUseEntry(entry) || !canReuseFamilySnapshotScope(entry.scope, requestedScope)) continue;
    if (
      !match ||
      entry.scope.parameterKeys.length < match.scope.parameterKeys.length ||
      (entry.scope.parameterKeys.length === match.scope.parameterKeys.length &&
        entry.scope.familyScopeKeys.length < match.scope.familyScopeKeys.length)
    ) {
      match = entry;
    }
  }
  return match;
}

export function cloneFamilySnapshotForScope(
  families: RevitFamily[],
  sourceScope: FamilySnapshotScope,
  requestedScope: FamilySnapshotScope
): RevitFamily[] {
  const isExactParameterScope = hasSameValues(sourceScope.parameterKeys, requestedScope.parameterKeys);
  const requestedFamilyKeys = new Set(requestedScope.familyScopeKeys);
  const scopedFamilies = hasSameValues(sourceScope.familyScopeKeys, requestedScope.familyScopeKeys)
    ? families
    : families.filter((family) => requestedFamilyKeys.has(family.id.trim().toLowerCase()));
  return cloneFamilySnapshot(
    scopedFamilies,
    isExactParameterScope ? undefined : new Set(requestedScope.parameterKeys)
  );
}

export function mergeScopedFamilySnapshot(
  broadFamily: RevitFamily,
  scopedFamily: RevitFamily,
  authoritativeParameterNames: string[] = []
): RevitFamily {
  const authoritativeParameterKeys = new Set(
    authoritativeParameterNames.map((parameterName) => parameterName.trim().toLowerCase())
  );
  const scopedTypesById = new Map(
    (scopedFamily.types ?? []).map((familyType) => [familyType.id.trim().toLowerCase(), familyType])
  );
  const scopedTypesByName = new Map(
    (scopedFamily.types ?? []).map((familyType) => [familyType.name.trim().toLowerCase(), familyType])
  );
  const matchedScopedTypeIds = new Set<string>();
  const types = (broadFamily.types ?? []).map((broadType) => {
    const scopedType = scopedTypesById.get(broadType.id.trim().toLowerCase()) ??
      scopedTypesByName.get(broadType.name.trim().toLowerCase());
    if (!scopedType) return broadType;

    matchedScopedTypeIds.add(scopedType.id.trim().toLowerCase());
    const scopedParameterKeys = new Set(
      (scopedType.parameterValues ?? []).map((parameter) =>
        parameter.parameterName.trim().toLowerCase()
      )
    );
    const parameterValues = (broadType.parameterValues ?? []).filter((parameter) => {
      const parameterKey = parameter.parameterName.trim().toLowerCase();
      return !authoritativeParameterKeys.has(parameterKey) || scopedParameterKeys.has(parameterKey);
    });
    const broadParameterIndexes = new Map(
      parameterValues.map((parameter, index) => [parameter.parameterName.trim().toLowerCase(), index])
    );
    (scopedType.parameterValues ?? []).forEach((scopedParameter) => {
      const parameterKey = scopedParameter.parameterName.trim().toLowerCase();
      const broadIndex = broadParameterIndexes.get(parameterKey);
      if (typeof broadIndex === 'undefined') {
        broadParameterIndexes.set(parameterKey, parameterValues.length);
        parameterValues.push(scopedParameter);
        return;
      }
      const broadParameter = parameterValues[broadIndex];
      parameterValues[broadIndex] = {
        ...broadParameter,
        ...scopedParameter,
        // Family-document metadata is authoritative, but the project type can
        // be the only source for an OBJECT-TYPE discriminator value.
        value: String(scopedParameter.value ?? '').trim().length > 0
          ? scopedParameter.value
          : broadParameter.value,
      };
    });
    return { ...broadType, ...scopedType, parameterValues };
  });
  (scopedFamily.types ?? []).forEach((scopedType) => {
    if (!matchedScopedTypeIds.has(scopedType.id.trim().toLowerCase())) types.push(scopedType);
  });
  return {
    ...broadFamily,
    ...scopedFamily,
    parameters: (scopedFamily.parameters ?? []).length > 0
      ? scopedFamily.parameters
      : broadFamily.parameters,
    types,
  };
}
